import React from 'react'
import classNames from 'classnames'
import { Link } from 'react-router-dom'

import { AnyCallback } from 'types/helpers'

import './_elo-button.scss'

type buttonTypes = 'button' | 'submit' | 'reset'
interface FloatingProps {
  id?: string | number
  onClick?: AnyCallback
  className?: string
  disabled?: boolean
  type?: buttonTypes
  children?: React.ReactNode
  to?: string
}
const Floating = (props: FloatingProps) => {
  const { children, className, disabled, id, onClick, type = 'button' } = props

  const buttonClasses = classNames('elo-btn elo-btn--floating', className)

  return (
    <button
      className={buttonClasses}
      type={type}
      {...{
        disabled,
        id: String(id),
        onClick,
      }}
    >
      {children}
    </button>
  )
}

interface DefaultProp {
  id?: string | number
  onClick?: AnyCallback
  href?: string
  to?: string
  className?: string
  copy?: boolean
  outlined?: boolean
  grey?: boolean
  orange?: boolean
  overlayButton?: boolean
  white?: boolean
  green?: boolean
  transparent?: boolean
  disabled?: boolean
  download?: boolean
  type?: buttonTypes
  downloadOnClick?: boolean
  long?: boolean
  rectangle?: boolean
  share?: boolean
  children?: React.ReactNode
  pink?: boolean
  style?: React.CSSProperties
  target?: string
  LinkComponent?: any
  onMouseEnter?: AnyCallback
  onMouseLeave?: AnyCallback
  dataTestId?: string
  ariaLabel?: string
}
const Default = (props: DefaultProp) => {
  const {
    children,
    className,
    copy = false,
    disabled = false,
    download = false,
    downloadOnClick = false,
    green,
    grey = false,
    href,
    id,
    long,
    onClick,
    orange = false,
    outlined = false,
    overlayButton = false,
    pink,
    rectangle,
    style,
    share,
    target,
    to,
    type = 'button',
    white = false,
    dataTestId,
    LinkComponent = Link,
    onMouseEnter,
    onMouseLeave,
    ariaLabel,
  } = props

  const buttonClasses = classNames(
    'elo-btn',
    {
      blue: !orange && !green && !outlined && !download && !pink && !copy && !grey,
      grey,
      orange,
      green,
      pink,
      'download outline-blue': download,
      'copy outline-blue': copy,
      'outline-blue': outlined && !orange && !grey && !white && !pink && !green,
      'outline-white': outlined && white,
      'outline-grey': outlined && grey,
      'outline-pink': outlined && pink,
      'outline-green--disabled': outlined && green,
      'elo-btn--disabled': disabled,
      overlay: overlayButton,
      long,
      rectangle,
      share,
    },
    className
  )

  return (
    <>
      {(onClick || type === 'submit') && (
        <button
          className={buttonClasses}
          {...{
            disabled,
            id: String(id),
            onClick,
            style,
            type,
            onMouseEnter,
            onMouseLeave,
            'data-testid': dataTestId ?? type,
            'data-test-id': dataTestId ?? type,
            ariaLabel,
          }}
        >
          {copy && <i className='far fa-copy' />}
          {download && <i className='fas fa-download' />}
          {children}
        </button>
      )}
      {href && (
        <a
          className={buttonClasses}
          download={downloadOnClick}
          href={href}
          {...{
            id: String(id),
            style,
            target,
          }}
        >
          {copy && <i className='far fa-copy' />}
          {download && <i className='fas fa-download' />}
          {children}
        </a>
      )}
      {to && (
        <LinkComponent
          {...{
            id: String(id),
            style: {
              ...style,
              ...(disabled && { pointerEvents: 'none' }),
            },
            target,
            to,
          }}
        >
          <button
            className={buttonClasses}
            {...{
              disabled,
            }}
          >
            {children}
          </button>
        </LinkComponent>
      )}
    </>
  )
}

/**
 * EloButton - button component
 */
type EloButtonProps = FloatingProps & DefaultProp
export const EloButton = (props: EloButtonProps) => <Default {...props} />

EloButton.displayName = 'EloButton'
EloButton.Floating = Floating
